import React, { forwardRef, Fragment, useImperativeHandle } from 'react';
import { View } from 'react-native';
import StyleSheet from 'react-native-media-query';

import useFormField from '../Hooks/useFormField';
import ThemeStyle from '../Theme/styles';
import breakpoints from '../Theme/styles/breakpoints';

import FormField from './FormFields/FormField';
import TextInput from './TextInputDebounce';

type Props = {
  disabled?: boolean;
  disableEmail?: boolean;
  internationalPhone?: boolean;
  useFormikContext?: boolean;
  values?: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  onChange?: (name: string, value: string) => void;
};

const ProfileForm: React.FC<Props> = forwardRef(
  (
    {
      disabled,
      disableEmail = true,
      internationalPhone,
      useFormikContext,
      values,
      onChange,
    }: Props,
    ref,
  ) => {
    const { getInputProps, setSubmitButtonRef } = useFormField();
    const InputElement = useFormikContext ? FormField : TextInput;
    const getConditionalProps = (name: string) =>
      useFormikContext
        ? {}
        : {
            value: values?.[name],
            onChange: (value: string) => onChange(name, value),
          };

    useImperativeHandle(ref, () => ({
      setSubmitButtonRef,
    }));

    return (
      <Fragment>
        <View
          dataSet={{ media: ids.inputRowContainer }}
          style={styles.inputRowContainer}
        >
          {/* First Name Input */}
          <View
            style={[
              ThemeStyle.flex1,
              !useFormikContext && ThemeStyle.spacingBottomMedium,
            ]}
          >
            <InputElement
              direction="column"
              disabled={disabled}
              label="First Name *"
              name="firstName"
              testID="firstName"
              withBorder
              {...getConditionalProps('firstName')}
              {...getInputProps(1)}
            />
          </View>
          {/* Separator */}
          <View dataSet={{ media: ids.separator }} style={styles.separator} />
          {/* Last Name Input */}
          <View
            style={[
              ThemeStyle.flex1,
              !useFormikContext && ThemeStyle.spacingBottomMedium,
            ]}
          >
            <InputElement
              direction="column"
              disabled={disabled}
              label="Last Name *"
              name="lastName"
              testID="lastName"
              withBorder
              {...getConditionalProps('lastName')}
              {...getInputProps(2)}
            />
          </View>
        </View>

        <View
          dataSet={{ media: ids.inputRowContainer }}
          style={styles.inputRowContainer}
        >
          {/* Phone Number Input */}
          <View
            style={[
              ThemeStyle.flex1,
              !useFormikContext && ThemeStyle.spacingBottomMedium,
            ]}
          >
            <InputElement
              direction="column"
              disabled={disabled}
              internationalPhone={internationalPhone}
              label="Phone Number *"
              name="phone"
              testID="phone"
              type="phone"
              withBorder
              {...getConditionalProps('phone')}
              {...getInputProps(3, disableEmail)}
            />
          </View>
          {/* Separator */}
          <View dataSet={{ media: ids.separator }} style={styles.separator} />
          {/* Email Input */}
          <View
            style={[
              ThemeStyle.flex1,
              !useFormikContext && ThemeStyle.spacingBottomMedium,
            ]}
          >
            <InputElement
              direction="column"
              label="Email *"
              name="email"
              testID="email"
              type="email"
              disabled={disableEmail || disabled}
              withBackground={disableEmail}
              withBorder={!disableEmail}
              {...getConditionalProps('email')}
              {...(!disableEmail && getInputProps(4, true))}
            />
          </View>
        </View>
      </Fragment>
    );
  },
);

const { ids, styles } = StyleSheet.create({
  inputRowContainer: {
    [`@media ${breakpoints.sm}`]: ThemeStyle.flexDirectionRowCenter,
  },
  separator: {
    [`@media ${breakpoints.sm}`]: ThemeStyle.rowSeparator,
  },
});

export default ProfileForm;
